import { useMemo } from "react";
import { sortAlphabetically } from "@features/vessel-call/grid/utils";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import type { Company, FromToLocation } from "@services/vessel-call/types";
import type { Country, Port, VesselType } from "@services/vessel/types";

export interface QueueFilterOption {
  label: string;
  value: string;
  key: string;
}

const VESSEL_SERVICE = "bff-vessel";
const VESSEL_CALL_SERVICE = "bff-vessel-call";

const toOptions = <T>(
  items: T[],
  code: (item: T) => string | null | undefined,
  label: (item: T) => string | null | undefined,
): QueueFilterOption[] => {
  const byCode = new Map<string, QueueFilterOption>();
  for (const item of items) {
    const itemCode = code(item);
    const itemLabel = label(item)?.trim();
    if (itemCode && itemLabel && !byCode.has(itemCode)) {
      byCode.set(itemCode, { label: itemLabel, value: itemCode, key: itemCode });
    }
  }
  return sortAlphabetically([...byCode.values()]);
};

export const vesselTypeOptions = (items: VesselType[]) =>
  toOptions(
    items,
    (item) => item?.vesselTypeCode,
    (item) => item?.vesselTypeDesc,
  );

export const flagOptions = (items: Country[]) =>
  toOptions(
    items,
    (item) => item?.countryCode,
    (item) => item?.countryName,
  );

export const locationOptions = (items: FromToLocation[]) =>
  toOptions(
    items,
    (item) => item?.fromToLocationCode,
    (item) => item?.locationName,
  );

export const agencyOptions = (items: Company[]) =>
  toOptions(
    items,
    (item) => item?.companyCode,
    (item) => item?.companyNameEnglish,
  );

const normalisedName = (name: string) => name.trim().toLowerCase();

export const portOptions = (items: Port[]) => {
  const options = toOptions(
    items,
    (item) => item?.portCode,
    (item) => item?.portName,
  );
  const occurrences = new Map<string, number>();
  for (const { label } of options) {
    occurrences.set(normalisedName(label), (occurrences.get(normalisedName(label)) ?? 0) + 1);
  }
  return sortAlphabetically(
    options.map((option) =>
      (occurrences.get(normalisedName(option.label)) ?? 0) > 1
        ? { ...option, label: `${option.label} (${option.value})` }
        : option,
    ),
  );
};

export const useQueueFilterOptions = () => {
  const { options: vesselTypes = [] } = useFormattedReference<VesselType>(
    REFERENCE_DATA_TYPES.VESSEL_TYPES,
    VESSEL_SERVICE,
  );
  const { options: countries = [] } = useFormattedReference<Country>(
    REFERENCE_DATA_TYPES.COUNTRIES,
    VESSEL_SERVICE,
  );
  const { options: ports = [] } = useFormattedReference<Port>(
    REFERENCE_DATA_TYPES.PORTS,
    VESSEL_SERVICE,
  );
  const { options: locations = [] } = useFormattedReference<FromToLocation>(
    REFERENCE_DATA_TYPES.FROM_TO_LOCATIONS,
    VESSEL_CALL_SERVICE,
  );
  const { options: companies = [] } = useFormattedReference<Company>(
    `${REFERENCE_DATA_TYPES.COMPANIES}?isShippingAgency=true`,
    VESSEL_CALL_SERVICE,
  );

  return useMemo(
    () => ({
      vesselTypes: vesselTypeOptions(vesselTypes),
      flags: flagOptions(countries),
      ports: portOptions(ports),
      locations: locationOptions(locations),
      agencies: agencyOptions(companies),
    }),
    [vesselTypes, countries, ports, locations, companies],
  );
};
